import React from "react";
import { useTranslation } from "react-i18next";
import CustomDigitalTwinTabs from "./CustomDigitalTwinTabs";
import CustomDigitalTwinHeader from "./CustomDigitalTwinHeader";
import PrimaryTabPanel from "../../../Features/Tabs/PrimaryTabPanel";
import { decryptTheParams } from "../../../Helper/QueryParams/EncryptDecrypt";
import { Box } from "@mui/material";
import CustomBatteryOverview from "./Tabs/CustomBatteryOverview";
import CustomBatteryAlerts from "./Tabs/CustomBatteryAlerts";
import CustomBatteryPerformance from "./Tabs/CustomBatteryPerformance";
import { usePermissions } from "../../../Context/PermissionContext";
import { FEATURE_CODES } from "../../../Static/FeatureMapping";

const CHARGING_EVENT_TYPES = ["charging"];

const ALL_TABS = [
    { slug: "overview", labelKey: "digital_twin:battery_overview", defaultLabel: "Overview" },
    { slug: "alerts", labelKey: "digital_twin:alerts", defaultLabel: "Alerts" },
    { slug: "performance", labelKey: "digital_twin:performance", defaultLabel: "Performance" },
    { slug: "ChargingPerformance", labelKey: "digital_twin:performance", defaultLabel: "Performance" },
];

const getActiveTab = (queryParams, clientTabs) => {
    const currentTabValue = queryParams.tabValue;
    if (clientTabs?.length && !clientTabs.includes(currentTabValue)) {
        return clientTabs[0];
    }
    const allSlugs = ALL_TABS.map(t => t.slug);
    if (currentTabValue && allSlugs.includes(currentTabValue)) {
        return currentTabValue;
    }
    return clientTabs?.[0] || allSlugs[0];
};

/**
 * Custom platform Digital Twin main layout.
 *
 * Renders only the tabs listed in pagesContent.metaInfo.tabsToShow, using
 * custom tab components (CustomBatteryOverview, CustomBatteryAlerts,
 * CustomBatteryPerformance) that respect org meta configuration.
 */
function CustomDigitalTwinMain({ pagesContent }) {
    const { t } = useTranslation(["digital_twin"]);
    const queryParams = decryptTheParams();
    const clientTabs = pagesContent.metaInfo.tabsToShow || [];
    const { hasPermission, isRbacV2Enabled } = usePermissions();
    const bmsAlertsEnabled = !isRbacV2Enabled() || hasPermission(FEATURE_CODES.INDIGO_OBSERVABILITY_PLATFORM_BMS_ALERTS, 'view');
    const [selectedTab, setSelectedTab] = React.useState(getActiveTab(queryParams, clientTabs));

    const tabsToShow = ALL_TABS
        .filter(tab => !clientTabs.length || clientTabs.includes(tab.slug))
        .filter(tab => tab.slug !== 'alerts' || bmsAlertsEnabled)
        .map((tab, idx) => ({
            id: idx,
            name: t(tab.labelKey, tab.defaultLabel),
            slug: tab.slug,
            disabled: false,
        }));

    return (
        <div style={{ height: '100vh', paddingTop: '14px', paddingLeft: '16px', paddingRight: '16px' }}>
            <CustomDigitalTwinHeader />

            <Box sx={{
                position: 'sticky',
                top: '50px',
                backgroundColor: '#FAFAFA',
                marginLeft: '-16px',
                marginRight: '-16px',
                zIndex: (theme) => theme.zIndex.appBar + 1,
            }}>
                <CustomDigitalTwinTabs
                    tabs={tabsToShow}
                    changeTheTab={(tabId) => setSelectedTab(tabId)}
                    selectedTab={selectedTab}
                />
            </Box>

            <PrimaryTabPanel value={selectedTab} id="overview" key={0}>
                <CustomBatteryOverview />
            </PrimaryTabPanel>

            <PrimaryTabPanel value={selectedTab} id="alerts" key={1}>
                <CustomBatteryAlerts />
            </PrimaryTabPanel>

            <PrimaryTabPanel value={selectedTab} id="performance" key={2}>
                <CustomBatteryPerformance />
            </PrimaryTabPanel>

            <PrimaryTabPanel value={selectedTab} id="ChargingPerformance" key={3}>
                <CustomBatteryPerformance allowedEventTypes={CHARGING_EVENT_TYPES} />
            </PrimaryTabPanel>
        </div>
    );
}

export default CustomDigitalTwinMain;
